/**
 * A record's history by day (ACL-508): the timeline's entries under "Today", "Yesterday" and then
 * their dates, each entry at its time of day. The days are the viewer's own, on their browser's
 * clock, unless a zone is named (the tests name one).
 */

export interface DayOptions {
  /** An IANA zone; the browser's own when left out. */
  zone?: string;
  /** The words' language; the browser's when left out. */
  locale?: string;
}

export interface DayGroup<T> {
  /** The day as `YYYY-MM-DD` in the zone, or `""` for entries with no readable time. */
  key: string;
  heading: string;
  items: T[];
}

/** The calendar day an instant falls on in the zone, as `YYYY-MM-DD`; null for no readable time. */
export function dayKey(at: string | Date, { zone }: DayOptions = {}): string | null {
  const d = typeof at === "string" ? new Date(at) : at;
  if (Number.isNaN(d.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: zone, year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(d);
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

/** The day before a `YYYY-MM-DD`, by the calendar: a clock change does not move it. */
function dayBefore(key: string): string {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(Date.UTC(y!, m! - 1, d! - 1)).toISOString().slice(0, 10);
}

/** A day's heading: "Today", "Yesterday", "Thu, Oct 15", and "Wed, Oct 15, 2025" in another year. */
export function dayHeading(key: string, now: Date = new Date(), options: DayOptions = {}): string {
  if (!key) return "Date unknown";
  const today = dayKey(now, options);
  if (key === today) return "Today";
  if (today && key === dayBefore(today)) return "Yesterday";
  const [y, m, d] = key.split("-").map(Number);
  // Noon on that day, read in UTC: the date's own words, whatever the viewer's zone.
  const noon = new Date(Date.UTC(y!, m! - 1, d!, 12));
  return new Intl.DateTimeFormat(options.locale, {
    timeZone: "UTC", weekday: "short", month: "short", day: "numeric",
    year: today?.slice(0, 4) === key.slice(0, 4) ? undefined : "numeric",
  }).format(noon);
}

/**
 * Entries under their days, in the order given (newest first, as the timeline reads), each day once
 * however the pages it came in split it. A day with no entries has no group.
 */
export function groupByDay<T>(
  items: readonly T[],
  at: (item: T) => string,
  now: Date = new Date(),
  options: DayOptions = {},
): DayGroup<T>[] {
  const groups = new Map<string, DayGroup<T>>();
  for (const item of items) {
    const key = dayKey(at(item), options) ?? "";
    let group = groups.get(key);
    if (!group) {
      group = { key, heading: dayHeading(key, now, options), items: [] };
      groups.set(key, group);
    }
    group.items.push(item);
  }
  return [...groups.values()];
}

/** The time of day an entry happened, as its day's heading leaves it to say: "9:42 AM". */
export function timeOfDay(at: string, { zone, locale }: DayOptions = {}): string {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat(locale, { timeZone: zone, hour: "numeric", minute: "2-digit" }).format(d);
}
